// export default VideoGeneratorPage;
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import { extractErrorMessage } from '../utils/errorHandler'
import GenerationLoader from '../components/GenerationLoader'
import GenerationHistory from '../components/GenerationHistory'
import LengthSelector from '../components/LengthSelector'
import {
  FiVideo,
  FiLoader,
  FiAlertTriangle,
  FiArrowLeft,
  FiDownload,
  FiZap,
  FiClock,
} from 'react-icons/fi'
import {
  triggerVideoDownload,
  getVideoModels,
  checkImageToVideoAvailability,
  generateVideo,
  interpolateVideo,
  extendVideo,
  createVideoViewUrl,
  getVideoHistory,
} from '../services/videoService'
import * as generationService from '../services/generationService'
import './VideoGeneratorPage.css'

const VideoGeneratorPage = () => {
  const { token } = useAuthContext()
  const navigate = useNavigate()

  // Use refs to avoid React reconciliation issues
  const videoBlobUrlRef = useRef(null)
  const isComponentMounted = useRef(true)

  // Form state
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState('16:9')
  const [selectedModel, setSelectedModel] = useState('advanced')
  const [negativePrompt, setNegativePrompt] = useState('')
  const [resolution, setResolution] = useState('720p')

  // Tab state
  const [activeTab, setActiveTab] = useState('generate') // "generate", "interpolate", "extend"

  // File upload states
  const [firstFrame, setFirstFrame] = useState(null)
  const [lastFrame, setLastFrame] = useState(null)
  const [firstFramePreview, setFirstFramePreview] = useState(null)
  const [lastFramePreview, setLastFramePreview] = useState(null)
  const [videoFile, setVideoFile] = useState(null)

  // Generation state - keep it simple
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState('')

  // Video result state - simplified
  const [videoResult, setVideoResult] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [generationTime, setGenerationTime] = useState(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const startTimeRef = useRef(null)
  const timerIntervalRef = useRef(null)

  // API data state
  const [availableModels, setAvailableModels] = useState({})
  const [isLoadingData, setIsLoadingData] = useState(true)

  // Magic prompt state
  const [isMagicLoading, setIsMagicLoading] = useState(false)
  const [magicPromptLength, setMagicPromptLength] = useState('medium')

  // History states
  const [videoHistory, setVideoHistory] = useState([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)

  // Safe state setter
  const safeSetState = React.useCallback((setter, value) => {
    if (isComponentMounted.current) {
      setter(value)
    }
  }, [])

  // Cleanup function for blob URLs
  const cleanupBlobUrls = () => {
    if (videoBlobUrlRef.current) {
      try {
        URL.revokeObjectURL(videoBlobUrlRef.current)
      } catch (e) {
        console.warn('Error revoking blob URL:', e)
      }
      videoBlobUrlRef.current = null
    }
  }

  // Fetch video history
  const fetchHistory = React.useCallback(async () => {
    if (!token) return
    safeSetState(setIsLoadingHistory, true)
    try {
      const history = await getVideoHistory(token, 5, 60)
      safeSetState(setVideoHistory, history)
    } catch (error) {
      console.error('Failed to load video history:', error)
    } finally {
      safeSetState(setIsLoadingHistory, false)
    }
  }, [token, safeSetState])

  // Load API data on component mount
  useEffect(() => {
    isComponentMounted.current = true

    const loadApiData = async () => {
      if (!token) return

      try {
        setIsLoadingData(true)

        const [modelsResponse] = await Promise.all([
          getVideoModels(token).catch(err => {
            console.warn('Failed to load models:', err)
            return { available_models: {} }
          }),
          checkImageToVideoAvailability(token).catch(err => {
            console.warn('Failed to check availability:', err)
            return { image_to_video_available: true, message: '' }
          }),
        ])

        if (isComponentMounted.current) {
          setAvailableModels(modelsResponse.available_models || {})
        }
      } catch (error) {
        console.error('Error loading API data:', error)
      } finally {
        if (isComponentMounted.current) {
          setIsLoadingData(false)
        }
      }
    }

    loadApiData()
    fetchHistory() // Load history on mount

    // Cleanup on unmount
    return () => {
      isComponentMounted.current = false
      cleanupBlobUrls()
    }
  }, [token, fetchHistory])

  const handleBack = () => {
    navigate(-1)
  }

  const handleMagicPrompt = async () => {
    if (!prompt.trim() || isGenerating || isMagicLoading) return

    safeSetState(setIsMagicLoading, true)
    safeSetState(setError, '')
    try {
      const enhancedPrompt = await generationService.getMagicPrompt(
        token,
        prompt,
        magicPromptLength
      )
      safeSetState(setPrompt, enhancedPrompt)
    } catch (err) {
      const errorMessage = extractErrorMessage(
        err,
        'Failed to generate magic prompt. Please try again.'
      )
      safeSetState(setError, errorMessage)
    } finally {
      safeSetState(setIsMagicLoading, false)
    }
  }

  const handleDownload = async (downloadUrl, filename) => {
    if (!downloadUrl || !token || isDownloading) return

    safeSetState(setIsDownloading, true)
    try {
      await triggerVideoDownload(token, filename, `generated_video_${Date.now()}.mp4`)
    } catch (error) {
      console.error('Download failed:', error)
      safeSetState(setError, `Download failed: ${error.message}`)
    } finally {
      safeSetState(setIsDownloading, false)
    }
  }

  const handleGenerate = async e => {
    e.preventDefault()
    if (!prompt.trim() || isGenerating || !token) return

    // Reset states and clean up URLs
    cleanupBlobUrls()
    safeSetState(setError, '')
    safeSetState(setVideoResult, null)
    safeSetState(setIsGenerating, true)
    safeSetState(setGenerationTime, null)
    safeSetState(setElapsedSeconds, 0)
    startTimeRef.current = Date.now()

    // Start timer - update every second
    timerIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        safeSetState(setElapsedSeconds, elapsed)
      }
    }, 1000)

    try {
      const form = new FormData()
      form.append('prompt', prompt)
      form.append('model', selectedModel)
      form.append('aspect_ratio', aspectRatio)
      form.append('resolution', resolution)

      if (negativePrompt.trim()) {
        form.append('negative_prompt', negativePrompt)
      }

      const response = await generateVideo(token, form)

      if (!response.success) {
        throw new Error(
          response.message ||
            response.error ||
            'F.R.A.N.C.A. AI video generation failed on the server.'
        )
      }

      // Create blob URL for viewing
      const filename = response.video_url?.split('/').pop() || `video_${Date.now()}`
      const blobUrl = await createVideoViewUrl(token, filename)
      videoBlobUrlRef.current = blobUrl

      // Create a stable result object
      const videoResult = {
        viewUrl: blobUrl,
        downloadUrl: response.download_url,
        message: response.message,
        filename: filename,
      }

      // Calculate generation time
      if (startTimeRef.current) {
        const timeTaken = ((Date.now() - startTimeRef.current) / 1000).toFixed(1)
        safeSetState(setGenerationTime, parseFloat(timeTaken))
      }

      safeSetState(setVideoResult, videoResult)

      // Refresh history after successful generation
      fetchHistory()
    } catch (err) {
      console.error('F.R.A.N.C.A. AI video generation error:', err)

      let errorMessage =
        err.message || 'An error occurred during F.R.A.N.C.A. AI video generation.'

      if (
        errorMessage.includes('not supported') ||
        errorMessage.includes('not available')
      ) {
        errorMessage =
          'Image-to-video is not available in your region. Try generating without an image - the system will create one automatically!'
      } else if (errorMessage.includes('validation error')) {
        errorMessage =
          'There was an issue with the request format. Please try again or contact support if the problem persists.'
      }

      safeSetState(setError, errorMessage)
    } finally {
      safeSetState(setIsGenerating, false)
    }
  }

  // File upload handlers
  const handleFirstFrameChange = e => {
    const file = e.target.files[0]
    if (file) {
      setFirstFrame(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setFirstFramePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleLastFrameChange = e => {
    const file = e.target.files[0]
    if (file) {
      setLastFrame(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setLastFramePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleVideoFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      setVideoFile(file)
    }
  }

  const handleInterpolate = async e => {
    e.preventDefault()
    if (!prompt.trim() || !firstFrame || !lastFrame || isGenerating || !token) return

    cleanupBlobUrls()
    safeSetState(setError, '')
    safeSetState(setVideoResult, null)
    safeSetState(setIsGenerating, true)
    safeSetState(setGenerationTime, null)
    safeSetState(setElapsedSeconds, 0)
    startTimeRef.current = Date.now()

    // Start timer - update every second
    timerIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        safeSetState(setElapsedSeconds, elapsed)
      }
    }, 1000)

    try {
      const form = new FormData()
      form.append('prompt', prompt)
      form.append('first_frame', firstFrame)
      form.append('last_frame', lastFrame)
      form.append('model', selectedModel)
      form.append('aspect_ratio', aspectRatio)
      form.append('resolution', resolution)

      if (negativePrompt.trim()) {
        form.append('negative_prompt', negativePrompt)
      }

      const response = await interpolateVideo(token, form)

      if (!response.success) {
        throw new Error(
          response.message ||
            response.error ||
            'F.R.A.N.C.A. AI video interpolation failed on the server.'
        )
      }

      // Create blob URL for viewing
      const filename = response.video_url?.split('/').pop() || `video_${Date.now()}`
      const blobUrl = await createVideoViewUrl(token, filename)
      videoBlobUrlRef.current = blobUrl

      const videoResult = {
        viewUrl: blobUrl,
        downloadUrl: response.download_url,
        message: response.message,
        filename: filename,
      }

      // Calculate generation time
      if (startTimeRef.current) {
        const timeTaken = ((Date.now() - startTimeRef.current) / 1000).toFixed(1)
        safeSetState(setGenerationTime, parseFloat(timeTaken))
      }

      safeSetState(setVideoResult, videoResult)
    } catch (err) {
      console.error('F.R.A.N.C.A. AI video interpolation error:', err)
      let errorMessage =
        err.message ||
        err.response?.data?.detail ||
        'An error occurred during F.R.A.N.C.A. AI video interpolation.'

      if (
        errorMessage.includes('not supported') ||
        errorMessage.includes('not available')
      ) {
        errorMessage =
          'Video interpolation is not available. Please try again or contact support.'
      }

      safeSetState(setError, errorMessage)
    } finally {
      safeSetState(setIsGenerating, false)
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
      startTimeRef.current = null
    }
  }

  const handleExtend = async e => {
    e.preventDefault()
    if (!prompt.trim() || !videoFile || isGenerating || !token) return

    cleanupBlobUrls()
    safeSetState(setError, '')
    safeSetState(setVideoResult, null)
    safeSetState(setIsGenerating, true)
    safeSetState(setGenerationTime, null)
    safeSetState(setElapsedSeconds, 0)
    startTimeRef.current = Date.now()

    // Start timer - update every second
    timerIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        safeSetState(setElapsedSeconds, elapsed)
      }
    }, 1000)

    try {
      const form = new FormData()
      form.append('prompt', prompt)
      form.append('video', videoFile)
      form.append('model', selectedModel)
      form.append('aspect_ratio', aspectRatio)
      form.append('resolution', resolution)

      if (negativePrompt.trim()) {
        form.append('negative_prompt', negativePrompt)
      }

      const response = await extendVideo(token, form)

      if (!response.success) {
        throw new Error(
          response.message ||
            response.error ||
            'F.R.A.N.C.A. AI video extension failed on the server.'
        )
      }

      // Create blob URL for viewing
      const filename = response.video_url?.split('/').pop() || `video_${Date.now()}`
      const blobUrl = await createVideoViewUrl(token, filename)
      videoBlobUrlRef.current = blobUrl

      const videoResult = {
        viewUrl: blobUrl,
        downloadUrl: response.download_url,
        message: response.message,
        filename: filename,
      }

      // Calculate generation time
      if (startTimeRef.current) {
        const timeTaken = ((Date.now() - startTimeRef.current) / 1000).toFixed(1)
        safeSetState(setGenerationTime, parseFloat(timeTaken))
      }

      safeSetState(setVideoResult, videoResult)
    } catch (err) {
      console.error('F.R.A.N.C.A. AI video extension error:', err)
      let errorMessage =
        err.message ||
        err.response?.data?.detail ||
        'An error occurred during F.R.A.N.C.A. AI video extension.'

      // Special error handling for FRANCA-only validation
      if (
        errorMessage.includes('only veo') ||
        errorMessage.includes('only FRANCA') ||
        errorMessage.includes('not generated by FRANCA')
      ) {
        errorMessage =
          "This video wasn't generated by FRANCA AI. Only FRANCA-generated videos can be extended."
      } else if (
        errorMessage.includes('not supported') ||
        errorMessage.includes('not available')
      ) {
        errorMessage =
          'Video extension is not available. Please try again or contact support.'
      }

      safeSetState(setError, errorMessage)
    } finally {
      safeSetState(setIsGenerating, false)
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
      startTimeRef.current = null
    }
  }

  // Simple Video Player Component with error boundary
  const VideoPlayer = ({ src }) => {
    const [hasError, setHasError] = useState(false)
    const videoRef = useRef(null)

    // Update video source when src changes without remounting
    useEffect(() => {
      if (videoRef.current && src) {
        videoRef.current.load()
      }
    }, [src])

    if (hasError) {
      return (
        <div className="video-error">
          <p>Unable to load video preview</p>
          <button onClick={() => setHasError(false)}>Retry</button>
        </div>
      )
    }

    return (
      <div className="video-player-container">
        <video
          ref={videoRef}
          controls
          playsInline
          width="100%"
          style={{ maxWidth: '600px', borderRadius: '8px' }}
          onError={() => setHasError(true)}
          preload="auto"
        >
          <source src={src} type="video/mp4" />
          Il tuo browser non supporta il tag video.
        </video>
      </div>
    )
  }

  return (
    <div className="generator-page-area">
      <div className="generator-header">
        <div className="header-controls">
          <button
            onClick={handleBack}
            className="back-button"
            aria-label="Torna indietro"
            type="button"
          >
            <FiArrowLeft />
          </button>
          <h1>
            <FiVideo size={10} /> Generatore Video
          </h1>
        </div>
        <p>
          Descrivi una storia o una scena e F.R.A.N.C.A. la trasformerà in un video. La
          generazione può richiedere diversi minuti.
        </p>
      </div>

      <div className="generator-content">
        <div className="generator-form-container">
          {/* Tabs */}
          <div className="video-tabs">
            <button
              type="button"
              className={`video-tab ${activeTab === 'generate' ? 'active' : ''}`}
              onClick={() => setActiveTab('generate')}
            >
              Genera Video
            </button>
            <button
              type="button"
              className={`video-tab ${activeTab === 'interpolate' ? 'active' : ''}`}
              onClick={() => setActiveTab('interpolate')}
            >
              Interpolate
            </button>
            <button
              type="button"
              className={`video-tab ${activeTab === 'extend' ? 'active' : ''}`}
              onClick={() => setActiveTab('extend')}
            >
              Estendi Video
            </button>
          </div>

          {/* Generate Tab */}
          {activeTab === 'generate' && (
            <form onSubmit={handleGenerate} className="generator-form">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Es: Un data center a 4 piani con tetto a pannelli solari, vista aerea che mostra l'infrastruttura tecnologica e i sistemi di raffreddamento..."
                className="generator-textarea"
                rows="4"
                disabled={isGenerating || isMagicLoading}
              />

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <LengthSelector
                  selectedLength={magicPromptLength}
                  onLengthChange={setMagicPromptLength}
                  disabled={isGenerating || isMagicLoading}
                />
                <button
                  type="button"
                  className="magic-prompt-button"
                  onClick={handleMagicPrompt}
                  disabled={!prompt.trim() || isGenerating || isMagicLoading}
                  title="Migliora il prompt con l'IA (Magic Prompt)"
                >
                  {isMagicLoading ? <FiLoader className="spinner-icon" /> : <FiZap />}
                  <span>Magic Prompt</span>
                </button>
              </div>

              <div className="form-controls">
                <div className="model-selector">
                  <label htmlFor="model">Modello:</label>
                  <select
                    id="model"
                    value={selectedModel}
                    onChange={e => setSelectedModel(e.target.value)}
                    disabled={isGenerating || isLoadingData}
                  >
                    {Object.keys(availableModels).length > 0 ? (
                      Object.entries(availableModels).map(([key, model]) => {
                        let displayName = 'Advanced Generation'
                        if (key === 'fast' || model.name === 'Fast Generation') {
                          displayName = 'Fast Generation'
                        } else if (
                          key === 'advanced' ||
                          model.name === 'Advanced Generation'
                        ) {
                          displayName = 'Advanced Generation'
                        }

                        return (
                          <option key={key} value={key}>
                            {displayName}
                          </option>
                        )
                      })
                    ) : (
                      <>
                        <option value="advanced">Advanced Generation</option>
                        <option value="fast">Fast Generation</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="aspect-ratio-selector">
                  <label htmlFor="aspect-ratio">Rapporto d'aspetto:</label>
                  <select
                    id="aspect-ratio"
                    value={aspectRatio}
                    onChange={e => setAspectRatio(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="16:9">16:9 (YouTube/Web)</option>
                    <option value="9:16">9:16 (Mobile/Stories)</option>
                  </select>
                  <div className="aspect-ratio-note">
                    <p>Square videos (1:1) not supported.</p>
                  </div>
                </div>

                <div className="resolution-selector">
                  <label htmlFor="resolution">Risoluzione:</label>
                  <select
                    id="resolution"
                    value={resolution}
                    onChange={e => setResolution(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="720p">720p - HD (faster, smaller file)</option>
                    <option value="1080p">1080p - Full HD (best quality)</option>
                  </select>
                </div>

                <details className="advanced-options">
                  <summary>Opzioni Avanzate</summary>
                  <div className="advanced-controls">
                    <div className="negative-prompt">
                      <label htmlFor="negative-prompt">
                        Prompt negativo (cosa evitare):
                      </label>
                      <input
                        type="text"
                        id="negative-prompt"
                        placeholder="Es: sfocato, distorto, di bassa qualità..."
                        value={negativePrompt}
                        onChange={e => setNegativePrompt(e.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                </details>
              </div>

              <button
                type="submit"
                className="generate-button"
                disabled={isGenerating || !prompt.trim()}
              >
                {isGenerating ? (
                  <>
                    <FiLoader className="spinner-icon" /> Generazione in corso...
                  </>
                ) : (
                  'Genera Video'
                )}
              </button>
            </form>
          )}

          {/* Interpolate Tab */}
          {activeTab === 'interpolate' && (
            <form onSubmit={handleInterpolate} className="generator-form">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Es: Transizione fluida dalla vista esterna del data center alla sala server interna, mostrando l'infrastruttura tecnologica..."
                className="generator-textarea"
                rows="4"
                disabled={isGenerating || isMagicLoading}
              />

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <LengthSelector
                  selectedLength={magicPromptLength}
                  onLengthChange={setMagicPromptLength}
                  disabled={isGenerating || isMagicLoading}
                />
                <button
                  type="button"
                  className="magic-prompt-button"
                  onClick={handleMagicPrompt}
                  disabled={!prompt.trim() || isGenerating || isMagicLoading}
                  title="Migliora il prompt con l'IA (Magic Prompt)"
                >
                  {isMagicLoading ? <FiLoader className="spinner-icon" /> : <FiZap />}
                  <span>Magic Prompt</span>
                </button>
              </div>

              <div className="image-upload-grid">
                <div className="image-upload-item">
                  <label htmlFor="first-frame">Primo Frame:</label>
                  <input
                    type="file"
                    id="first-frame"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFirstFrameChange}
                    disabled={isGenerating}
                  />
                  {firstFramePreview && (
                    <img
                      src={firstFramePreview}
                      alt="First frame preview"
                      className="image-preview"
                    />
                  )}
                </div>

                <div className="image-upload-item">
                  <label htmlFor="last-frame">Ultimo Frame:</label>
                  <input
                    type="file"
                    id="last-frame"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleLastFrameChange}
                    disabled={isGenerating}
                  />
                  {lastFramePreview && (
                    <img
                      src={lastFramePreview}
                      alt="Last frame preview"
                      className="image-preview"
                    />
                  )}
                </div>
              </div>

              <div className="form-controls">
                <div className="model-selector">
                  <label htmlFor="model-interpolate">Modello:</label>
                  <select
                    id="model-interpolate"
                    value={selectedModel}
                    onChange={e => setSelectedModel(e.target.value)}
                    disabled={isGenerating || isLoadingData}
                  >
                    {Object.keys(availableModels).length > 0 ? (
                      Object.entries(availableModels).map(([key, model]) => {
                        let displayName = 'Advanced Generation'
                        if (key === 'fast' || model.name === 'Fast Generation') {
                          displayName = 'Fast Generation'
                        } else if (
                          key === 'advanced' ||
                          model.name === 'Advanced Generation'
                        ) {
                          displayName = 'Advanced Generation'
                        }
                        return (
                          <option key={key} value={key}>
                            {displayName}
                          </option>
                        )
                      })
                    ) : (
                      <>
                        <option value="advanced">Advanced Generation</option>
                        <option value="fast">Fast Generation</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="aspect-ratio-selector">
                  <label htmlFor="aspect-ratio-interpolate">Rapporto d'aspetto:</label>
                  <select
                    id="aspect-ratio-interpolate"
                    value={aspectRatio}
                    onChange={e => setAspectRatio(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="16:9">16:9 (YouTube/Web)</option>
                    <option value="9:16">9:16 (Mobile/Stories)</option>
                  </select>
                  <div className="aspect-ratio-note">
                    <p>Square videos (1:1) not supported.</p>
                  </div>
                </div>

                <div className="resolution-selector">
                  <label htmlFor="resolution-interpolate">Risoluzione:</label>
                  <select
                    id="resolution-interpolate"
                    value={resolution}
                    onChange={e => setResolution(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="720p">720p - HD (faster, smaller file)</option>
                    <option value="1080p">1080p - Full HD (best quality)</option>
                  </select>
                </div>

                <details className="advanced-options">
                  <summary>Opzioni Avanzate</summary>
                  <div className="advanced-controls">
                    <div className="negative-prompt">
                      <label htmlFor="negative-prompt-interpolate">
                        Prompt negativo (cosa evitare):
                      </label>
                      <input
                        type="text"
                        id="negative-prompt-interpolate"
                        placeholder="Es: sfocato, distorto, di bassa qualità..."
                        value={negativePrompt}
                        onChange={e => setNegativePrompt(e.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                </details>
              </div>

              <button
                type="submit"
                className="generate-button"
                disabled={isGenerating || !prompt.trim() || !firstFrame || !lastFrame}
              >
                {isGenerating ? (
                  <>
                    <FiLoader className="spinner-icon" /> Interpolazione in corso...
                  </>
                ) : (
                  'Interpola Video'
                )}
              </button>
            </form>
          )}

          {/* Extend Tab */}
          {activeTab === 'extend' && (
            <form onSubmit={handleExtend} className="generator-form">
              <div className="extension-warning">
                <FiAlertTriangle />
                <p>
                  <strong>Nota:</strong> Questa funzionalità funziona solo con video
                  generati da FRANCA AI.
                </p>
              </div>

              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Es: Estendi il video mostrando l'area di backup e i sistemi di sicurezza del data center..."
                className="generator-textarea"
                rows="4"
                disabled={isGenerating || isMagicLoading}
              />

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <LengthSelector
                  selectedLength={magicPromptLength}
                  onLengthChange={setMagicPromptLength}
                  disabled={isGenerating || isMagicLoading}
                />
                <button
                  type="button"
                  className="magic-prompt-button"
                  onClick={handleMagicPrompt}
                  disabled={!prompt.trim() || isGenerating || isMagicLoading}
                  title="Migliora il prompt con l'IA (Magic Prompt)"
                >
                  {isMagicLoading ? <FiLoader className="spinner-icon" /> : <FiZap />}
                  <span>Magic Prompt</span>
                </button>
              </div>

              <div className="video-upload">
                <label htmlFor="video-file">Video (MP4 - solo FRANCA-generated):</label>
                <input
                  type="file"
                  id="video-file"
                  accept="video/mp4"
                  onChange={handleVideoFileChange}
                  disabled={isGenerating}
                />
                {videoFile && (
                  <div className="file-info">File selezionato: {videoFile.name}</div>
                )}
              </div>

              <div className="form-controls">
                <div className="model-selector">
                  <label htmlFor="model-extend">Modello:</label>
                  <select
                    id="model-extend"
                    value={selectedModel}
                    onChange={e => setSelectedModel(e.target.value)}
                    disabled={isGenerating || isLoadingData}
                  >
                    {Object.keys(availableModels).length > 0 ? (
                      Object.entries(availableModels).map(([key, model]) => {
                        let displayName = 'Advanced Generation'
                        if (key === 'fast' || model.name === 'Fast Generation') {
                          displayName = 'Fast Generation'
                        } else if (
                          key === 'advanced' ||
                          model.name === 'Advanced Generation'
                        ) {
                          displayName = 'Advanced Generation'
                        }
                        return (
                          <option key={key} value={key}>
                            {displayName}
                          </option>
                        )
                      })
                    ) : (
                      <>
                        <option value="advanced">Advanced Generation</option>
                        <option value="fast">Fast Generation</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="aspect-ratio-selector">
                  <label htmlFor="aspect-ratio-extend">Rapporto d'aspetto:</label>
                  <select
                    id="aspect-ratio-extend"
                    value={aspectRatio}
                    onChange={e => setAspectRatio(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="16:9">16:9 (YouTube/Web)</option>
                    <option value="9:16">9:16 (Mobile/Stories)</option>
                  </select>
                  <div className="aspect-ratio-note">
                    <p>Square videos (1:1) not supported.</p>
                  </div>
                </div>

                <div className="resolution-selector">
                  <label htmlFor="resolution-extend">Risoluzione:</label>
                  <select
                    id="resolution-extend"
                    value={resolution}
                    onChange={e => setResolution(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="720p">720p - HD (faster, smaller file)</option>
                    <option value="1080p">1080p - Full HD (best quality)</option>
                  </select>
                </div>

                <details className="advanced-options">
                  <summary>Opzioni Avanzate</summary>
                  <div className="advanced-controls">
                    <div className="negative-prompt">
                      <label htmlFor="negative-prompt-extend">
                        Prompt negativo (cosa evitare):
                      </label>
                      <input
                        type="text"
                        id="negative-prompt-extend"
                        placeholder="Es: sfocato, distorto, di bassa qualità..."
                        value={negativePrompt}
                        onChange={e => setNegativePrompt(e.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                </details>
              </div>

              <button
                type="submit"
                className="generate-button"
                disabled={isGenerating || !prompt.trim() || !videoFile}
              >
                {isGenerating ? (
                  <>
                    <FiLoader className="spinner-icon" /> Estensione in corso...
                  </>
                ) : (
                  'Estendi Video'
                )}
              </button>
            </form>
          )}
        </div>

        <div className="generator-output-container">
          {isGenerating && (
            <GenerationLoader
              type="video"
              elapsedSeconds={elapsedSeconds}
              message="Stiamo elaborando il tuo video. Potrebbe richiedere 1-2 minuti..."
            />
          )}

          {!isGenerating && videoResult && (
            <div className="video-result-container">
              <div className="results-header">
                <h3>Video Generato</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {generationTime && (
                    <span className="generation-time-badge">
                      <FiClock /> Generato in {generationTime}s
                    </span>
                  )}
                  <span className="results-count">✅ Completato</span>
                </div>
              </div>

              {videoResult.message && (
                <p className="result-message">{videoResult.message}</p>
              )}

              {videoResult.viewUrl && (
                <div className="video-card">
                  <VideoPlayer src={videoResult.viewUrl} />
                  <div className="video-actions">
                    {videoResult.downloadUrl && (
                      <button
                        onClick={() =>
                          handleDownload(videoResult.downloadUrl, videoResult.filename)
                        }
                        className="download-action-btn"
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <>
                            <FiLoader className="spinner-icon" />
                            <span>Scaricamento...</span>
                          </>
                        ) : (
                          <>
                            <FiDownload />
                            <span>Scarica Video</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isGenerating && !videoResult && !error && (
            <div className="idle-placeholder">
              <div className="idle-content">
                <FiVideo className="idle-icon" />
                <h3>Pronto per Creare</h3>
                <p>
                  {activeTab === 'generate'
                    ? "Inserisci un prompt descrittivo per generare video unici con l'IA."
                    : activeTab === 'interpolate'
                    ? 'Carica due immagini e descrivi la transizione tra di esse.'
                    : 'Carica un video FRANCA-generated e descrivi come estenderlo.'}
                </p>
                {isLoadingData && (
                  <p className="loading-info">
                    <FiLoader className="spinner-icon" /> Caricamento informazioni
                    modelli...
                  </p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="error-placeholder">
              <FiAlertTriangle className="error-icon" />
              <div className="error-content">
                <h3>Errore di Generazione</h3>
                <p>{error}</p>
              </div>
              {error.includes('not available') && (
                <div className="error-suggestion">
                  <p>
                    <strong>Suggerimento:</strong> Prova a generare senza caricare
                    un'immagine. Il sistema creerà automaticamente un'immagine di
                    partenza per te!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Recent History */}
          <GenerationHistory
            items={videoHistory}
            type="video"
            isLoading={isLoadingHistory}
            token={token}
            onItemClick={item => {
              // User can click to reload a previous prompt
              setPrompt(item.prompt || '')
              if (item.model) setSelectedModel(item.model)
              if (item.aspect_ratio) setAspectRatio(item.aspect_ratio)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default VideoGeneratorPage
